import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Sparkles, Pencil, Trash2, Plus, RotateCcw, Search, ChevronDown, ChevronUp } from "lucide-react";

export const DailyTipsManager = () => {
  const { t } = useTranslation("app");
  const [tips, setTips] = useState(null);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [draftIt, setDraftIt] = useState("");
  const [draftEn, setDraftEn] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () =>
    api.get("/daily-tips").then(({ data }) => setTips(data)).catch(() => setTips([]));
  useEffect(() => { load(); }, []);

  if (!tips) return null;

  const filtered = tips.filter(
    (x) =>
      !query ||
      x.text_it.toLowerCase().includes(query.toLowerCase()) ||
      (x.text_en || "").toLowerCase().includes(query.toLowerCase())
  );
  const activeCount = tips.filter((x) => x.active).length;

  const toggle = async (tip) => {
    try {
      const { data } = await api.put(`/daily-tips/${tip.id}`, { active: !tip.active });
      setTips((l) => l.map((x) => (x.id === tip.id ? data : x)));
    } catch { toast.error(t("set.tipsErr")); }
  };

  const openEdit = (tip) => {
    setEditing(tip);
    setDraftIt(tip.text_it);
    setDraftEn(tip.text_en || "");
  };
  const openAdd = () => {
    setAdding(true);
    setDraftIt("");
    setDraftEn("");
  };

  const save = async () => {
    if (!draftIt.trim()) { toast.error(t("set.tipsRequired")); return; }
    setBusy(true);
    try {
      if (adding) {
        const { data } = await api.post("/daily-tips", { text_it: draftIt, text_en: draftEn });
        setTips((l) => [...l, data]);
      } else {
        const { data } = await api.put(`/daily-tips/${editing.id}`, {
          text_it: draftIt, text_en: draftEn,
        });
        setTips((l) => l.map((x) => (x.id === editing.id ? data : x)));
      }
      toast.success(t("set.saved"));
      setEditing(null); setAdding(false);
    } catch { toast.error(t("set.tipsErr")); }
    finally { setBusy(false); }
  };

  const remove = async (tip) => {
    if (!window.confirm(t("set.tipsDeleteConfirm"))) return;
    try {
      await api.delete(`/daily-tips/${tip.id}`);
      setTips((l) => l.filter((x) => x.id !== tip.id));
      toast.success(t("set.saved"));
    } catch { toast.error(t("set.tipsErr")); }
  };

  const reset = async () => {
    if (!window.confirm(t("set.tipsResetConfirm"))) return;
    setBusy(true);
    try {
      await api.post("/daily-tips/reset");
      await load();
      toast.success(t("set.tipsResetDone"));
    } catch { toast.error(t("set.tipsErr")); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="daily-tips-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-heading font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" /> {t("set.tipsTitle")}
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {t("set.tipsSub", { total: tips.length, active: activeCount })}
          </p>
        </div>
        <div className="flex gap-2">
          {open && (
            <>
              <Button size="sm" onClick={openAdd} data-testid="tips-add-btn">
                <Plus className="h-4 w-4 mr-1" /> {t("set.tipsAdd")}
              </Button>
              <Button size="sm" variant="outline" onClick={reset} disabled={busy} data-testid="tips-reset-btn">
                <RotateCcw className="h-4 w-4 mr-1" /> {t("set.tipsReset")}
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} data-testid="tips-toggle-btn">
            {open ? <ChevronUp className="h-4 w-4 mr-1" /> : <ChevronDown className="h-4 w-4 mr-1" />}
            {open ? t("set.tipsHide") : t("set.tipsShow", { total: tips.length })}
          </Button>
        </div>
      </div>
      {open && (
      <>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("set.tipsSearch")}
          className="pl-9"
          data-testid="tips-search-input"
        />
      </div>
      <div className="max-h-96 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="tips-list">
        {filtered.map((tip, i) => (
          <div key={tip.id} className={`flex items-start gap-3 px-3 py-2.5 ${tip.active ? "" : "opacity-50"}`}>
            <span className="text-xs text-slate-400 font-mono mt-1 w-7 shrink-0">
              {tips.indexOf(tip) + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800">{tip.text_it}</div>
              {tip.text_en && (
                <div className="text-xs text-slate-400 italic mt-0.5">🇬🇧 {tip.text_en}</div>
              )}
              {tip.custom && (
                <span className="inline-block text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 rounded px-1.5 mt-1">
                  {t("set.tipsCustom")}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Switch
                checked={tip.active}
                onCheckedChange={() => toggle(tip)}
                data-testid={`tip-active-${tips.indexOf(tip) + 1}`}
              />
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(tip)} data-testid={`tip-edit-${tips.indexOf(tip) + 1}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => remove(tip)} data-testid={`tip-delete-${tips.indexOf(tip) + 1}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
        {!filtered.length && (
          <div className="p-4 text-center text-sm text-slate-500">{t("set.tipsNone")}</div>
        )}
      </div>
      </>
      )}

      <Dialog open={!!editing || adding} onOpenChange={(o) => { if (!o) { setEditing(null); setAdding(false); } }}>
        <DialogContent data-testid="tip-edit-dialog">
          <DialogHeader>
            <DialogTitle>{adding ? t("set.tipsAdd") : t("set.tipsEdit")}</DialogTitle>
            <DialogDescription className="sr-only">Daily Boost</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-bold text-slate-700">{t("set.tipsTextIt")}</label>
              <Textarea value={draftIt} onChange={(e) => setDraftIt(e.target.value)} rows={3} data-testid="tip-text-it-input" />
            </div>
            <div>
              <label className="text-sm font-bold text-slate-700">{t("set.tipsTextEn")}</label>
              <Textarea value={draftEn} onChange={(e) => setDraftEn(e.target.value)} rows={3} data-testid="tip-text-en-input" />
              <p className="text-xs text-slate-500 mt-1">{t("set.tipsTextEnHint")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setEditing(null); setAdding(false); }}>
              {t("set.tipsCancel")}
            </Button>
            <Button onClick={save} disabled={busy} data-testid="tip-save-btn">
              {t("set.tipsSave")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
